import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import {
  Event as RouterEvent,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ProgressBarComponent } from '../progress-bar/progress-bar.component';

@Component({
  selector: 'app-route-loader',
  standalone: true,
  imports: [ProgressBarComponent],
  template: `
    @if (active()) {
      <div class="kx-route-loader">
        <app-progress-bar  [value]="value()" [max]="100" [label]="{ visible: false }" />
      </div>
    }
  `,
  styles: [
    `
      .kx-route-loader {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        z-index: 9999;
        line-height: 0;
      }
      .kx-route-loader ::ng-deep .k-progressbar {
        height: 3px;
        border-radius: 0;
        background: transparent;
      }
      .kx-route-loader ::ng-deep .k-progressbar .k-selected {
        background: var(--h4c-bright-blue, #27aae1);
        transition: width 0.25s ease;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RouteLoaderComponent {
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  readonly active = signal(false);
  readonly value = signal(0);

  private trickleTimer?: ReturnType<typeof setInterval>;
  private completeTimer?: ReturnType<typeof setTimeout>;

  constructor() {
    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event: RouterEvent) => {
      if (event instanceof NavigationStart) {
        this.start();
      } else if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError
      ) {
        this.complete();
      }
    });

    this.destroyRef.onDestroy(() => this.clearTimers());
  }

  private start(): void {
    this.clearTimers();
    this.active.set(true);
    this.value.set(15);
    this.trickleTimer = setInterval(() => {
      const current = this.value();
      const remaining = 90 - current;
      if (remaining <= 0.5) {
        return;
      }
      this.value.set(Math.round(current + remaining * 0.25));
    }, 400);
  }

  private complete(): void {
    this.clearTimers();
    this.value.set(100);
    this.completeTimer = setTimeout(() => {
      this.active.set(false);
      this.value.set(0);
    }, 300);
  }

  private clearTimers(): void {
    if (this.trickleTimer) {
      clearInterval(this.trickleTimer);
      this.trickleTimer = undefined;
    }
    if (this.completeTimer) {
      clearTimeout(this.completeTimer);
      this.completeTimer = undefined;
    }
  }
}
